import {
  notEmpty,
  theme,
  useResponsive,
  useTrackImpressionEventByIntersection,
} from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { SwiperSlide } from "swiper/react";

import { ContentItemMagazinesStyleArticleMoreOnThis } from "~/components/content/content-item-render/variants/magazines-style-article-more-on-this";
import { useContentReadMoreContext } from "~/components/content/content-read-more";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { MoreOnThisWidgetEvents } from "~/components/tracking/google-analytics/types";
import { getAbsoluteUrl } from "~/lib/utils";
import type { articleMoreOnThisListStyleArticle$key } from "~/queries/__generated__/articleMoreOnThisListStyleArticle.graphql";

import { Container, ItemContainer, StyledSwiper, Title } from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreOnThisListStyleArticle$key;
};

export const ArticleMoreOnThisList: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const { styleMoreOnThisArticles } = useFragment(
    graphql`
      fragment articleMoreOnThisListStyleArticle on Article {
        styleMoreOnThisArticles: moreOnThisArticles {
          ...entityLink
          ...magazinesStyleArticleMoreOnThisContentItemContent
          entityId
          urlAlias
        }
      }
    `,
    reference_,
  );

  const articles = styleMoreOnThisArticles?.filter(notEmpty).slice(0, 5);

  const impressionEvent = useCallback<() => MoreOnThisWidgetEvents>(
    () => ({
      action: "More on this Story/Impression",
      category: "More on this Story",
      label: "",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    MoreOnThisWidgetEvents,
    null
  >({
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    options: { intersectionThreshold: [0.1], shouldThrottle: true },
  });

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), true);
  const { isReadMoreClicked } = useContentReadMoreContext();

  if (isMobile && !isReadMoreClicked) return null;
  if (!articles?.length) return null;

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <Title>
        / READ M<span>O</span>RE /
      </Title>
      <ItemContainer>
        <StyledSwiper
          $center={articles.length < 3}
          breakpoints={{
            1024: {
              noSwiping: false,
              slidesPerView: 3,
              spaceBetween: 32,
            },
            768: {
              noSwiping: false,
              slidesPerView: 3,
              spaceBetween: 20,
            },
          }}
          centeredSlidesBounds={true}
          noSwiping={true}
          pagination={false}
          slidesPerView={1}
        >
          {articles.map(article => (
            <SwiperSlide
              key={article.entityId}
              onClick={() => {
                sendGATracking({
                  action: "More on this Story/Click",
                  category: "More on this Story",
                  label: `${getAbsoluteUrl(article?.urlAlias)}`,
                });
              }}
            >
              <ContentItemMagazinesStyleArticleMoreOnThis reference={article} />
            </SwiperSlide>
          ))}
        </StyledSwiper>
      </ItemContainer>
    </Container>
  );
};

ArticleMoreOnThisList.displayName = "ArticleMoreOnThisList";
